import React, { memo, ReactElement, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { config, Spring } from 'react-spring/renderprops-universal';

import Tooltip from 'antd/lib/tooltip';

import { HidePassword, ShowPassword } from '@assets/images';
import { translate } from '@translations/Translater';

import Img from '@components//atoms/Image/Img';
import { globalMessages } from '@components/messages';
import Text, { FontFamilyVariant, TextType } from '@components/Text';

import './style.less';

interface IProps {
    value?: string | number;
    type?: 'text' | 'email' | 'number' | 'password' | 'url';
    id: string;
    placeholder?: string;
    label: string;
    onChange?: (e: any) => void;
    error?: string;
    onBlur?: () => void;
    className?: any;
    right?: ReactElement;
    disabled?: boolean;
    isPrivate?: boolean;
    isMandatory?: boolean;
    errorClass?: string;
    name: string;
    revealField?: boolean;
    isReadonly?: boolean;
}

export function AnimatedInput(props: IProps) {
    const inputRef = React.createRef();

    const {
        value,
        type,
        id,
        placeholder,
        label,
        onChange,
        error,
        onBlur,
        className,
        right = null,
        disabled,
        isPrivate,
        isMandatory,
        errorClass,
        name,
        revealField,
        isReadonly,
    } = props;

    // states
    const [reveal, setReveal] = React.useState(revealField || !!value);
    const [isInputFocus, setFocus] = React.useState(false);
    const [showPassword, toggleShowPassword] = React.useState(false);
    const { t } = useTranslation();

    const onClick = useCallback(() => {
        if (disabled) {
            return;
        }
        if (!isInputFocus || !reveal) {
            setReveal(true);
            if (inputRef && inputRef.current) {
                // @ts-ignore
                inputRef.current.focus();
            }
        }
    }, [isInputFocus, reveal, inputRef]);

    const setFocusFlag = useCallback(() => {
        if (!reveal) {
            setReveal(true);
        }
        setFocus(true);
    }, [reveal]);

    const onFocusOut = useCallback(() => {
        setFocusFlag();
        if (!value) {
            setReveal(revealField || false);
        }
        if (onBlur) {
            onBlur();
        }
    }, [onBlur, value]);

    const onValueChange = (e) => {
        if (onChange) {
            onChange(e);
        }
    };

    const togglePasswordVisibility = () => toggleShowPassword((prevShowPassword) => !prevShowPassword);

    const fieldIcon = () => {
        if (type === 'password') {
            const icon = showPassword ? ShowPassword : HidePassword;
            return (
                <div onClick={togglePasswordVisibility}>
                    <Img alt="show or hide password icon" className="icon" src={icon} />
                </div>
            );
        }
        if (isPrivate) {
            return (
                <Tooltip
                    color="#f9602c"
                    placement="left"
                    title={translate(t, globalMessages.wontShow)}
                    trigger={['hover', 'click']}
                >
                    <div className="private" />
                </Tooltip>
            );
        }
        return null;
    };

    const preventNonNumericalInput = (e: any) => {
        if (type !== 'number') return;
        e = e || window.event;
        const charCode = typeof e.which === 'undefined' ? e.keyCode : e.which;
        // if enter key pressed then allow for number type
        if (charCode === 13) {
            return;
        }
        const charStr = String.fromCharCode(charCode);
        if (!charStr.match(/^[0-9]+$/)) e.preventDefault();
    };

    return (
        <>
            <div className={`wf_animated_input ${className}`} onClick={onClick}>
                {label && (
                    <Spring
                        config={config.default}
                        from={{
                            top: 20,
                        }}
                        to={{
                            top: reveal || value ? -20 : 20,
                        }}
                    >
                        {(props) => (
                            <label
                                // @ts-ignore
                                style={props}
                            >
                                {label}
                                {isMandatory && (
                                    <span style={{ color: '#F9602C' }}>{' *'}</span>
                                )}
                            </label>
                        )}
                    </Spring>
                )}
                <Spring
                    config={config.default}
                    from={{
                        transform: value ? 'scaleY(1)' : 'scaleY(0)',
                    }}
                    to={{
                        transform: reveal || value ? 'scaleY(1)' : 'scaleY(0)',
                    }}
                >
                    {(props) => (
                        <input
                            // @ts-ignore
                            disabled={disabled}
                            id={id}
                            name={name}
                            onBlur={onFocusOut}
                            onChange={onValueChange}
                            onFocus={setFocusFlag}
                            // @ts-ignore
                            onKeyPress={preventNonNumericalInput}
                            placeholder={placeholder}
                            // @ts-ignore
                            ref={inputRef}
                            style={props}
                            type={showPassword ? 'text' : type || 'text'}
                            value={value || ''}
                            readOnly={isReadonly}
                        />
                    )}
                </Spring>
                {fieldIcon()}
                {right}
            </div>
            {error && <Text className={`validation-error ${errorClass}`} fontFamilyVariant={FontFamilyVariant.light} text={error} textType={TextType.paragraph1} />}
        </>
    );
}

export default memo(AnimatedInput);
